import { useState } from "react"
import { updateQrCode, saveContactPage, ApiError } from "../lib/api"
import { validateDestinationUrl } from "../lib/urls"
import ReCheckLink from "./ReCheckLink"
import MailtoSuggestion from "./MailtoSuggestion"
import DestinationInput from "./DestinationInput"
import useContactPage from "../hooks/useContactPage"
import ContactPageFields, { cleanContactPage, validateContactPage, contactPageErrorsFromServer } from "./ContactPageFields"
import ContactPageRemove from "./ContactPageRemove"

// The dashboard creator's inline destination edit. A hosted contact page
// (#332) edits its fields here (PUT .../contact_page); picking Website or Email
// repoints through the usual PATCH. `contactPages` offers the kind on a code
// that has no page.
export default function EditDestination({ code, onSave, onCancel, contactPages = false }) {
  const cp = useContactPage(code)
  const [value, setValue] = useState(code.contact_page ? "" : code.destination_url)
  const [status, setStatus] = useState("idle") // 'idle' | 'saving' | 'error'
  const [error, setError] = useState(null)
  const [appealUrl, setAppealUrl] = useState(null)
  const [suggestion, setSuggestion] = useState(null)

  async function saveHosted() {
    if (!cp.ready) return
    const pageErr = validateContactPage(cp.page)
    cp.setErrors(pageErr)
    if (pageErr) return
    if (code.contact_page && !cp.dirty) return onCancel()
    setStatus("saving")
    setError(null)
    setAppealUrl(null)
    try {
      const body = await saveContactPage(code.token, cleanContactPage(cp.page))
      onSave(body.code)
    } catch (e) {
      setStatus("error")
      if (e instanceof ApiError && e.field === "contact_page") {
        cp.setErrors(contactPageErrorsFromServer(e.details, e.message))
        setError(null)
        return
      }
      setError(e instanceof ApiError && e.message ? e.message : "Couldn't save. Try again.")
      setAppealUrl(e instanceof ApiError ? e.appealUrl : null)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (cp.hosted) return saveHosted()
    const { normalized, error: message, suggestion: fix } = validateDestinationUrl(value)
    setSuggestion(fix)
    if (message) {
      setStatus("error")
      setError(message)
      return
    }

    setStatus("saving")
    setError(null)
    setAppealUrl(null)
    try {
      const updated = await updateQrCode(code.token, normalized)
      onSave(updated)
    } catch (e) {
      setStatus("error")
      setError(
        e instanceof ApiError && e.message
          ? e.message
          : "Couldn't save. Try again."
      )
      setAppealUrl(e instanceof ApiError ? e.appealUrl : null)
    }
  }

  const saving = status === "saving"

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-2 mt-1">
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <DestinationInput
            value={value}
            onChange={setValue}
            disabled={saving}
            autoFocus
            contactPages={contactPages || code.contact_page}
            hosted={cp.hosted}
            onHostedChange={cp.setHosted}
            pickerClassName="border border-[color:var(--color-border)] dark:border-storm rounded pl-3 pr-2 py-1 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1"
            inputClassName="border border-[color:var(--color-border)] dark:border-storm rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1"
            inputProps={{ "aria-label": "Destination" }}
          />
        </div>
        <button
          type="submit"
          disabled={saving || (cp.hosted && !cp.ready)}
          className="bg-voltage text-coal rounded px-3 py-1 text-sm font-medium hover:bg-voltage-dark transition disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="text-[color:var(--color-muted)] dark:text-muted-light rounded px-2 py-1 text-sm hover:text-ink dark:hover:text-cloud"
        >
          Cancel
        </button>
      </div>
      {cp.hosted && (
        cp.ready ? (
          <ContactPageFields page={cp.page} onChange={cp.setPage} errors={cp.errors} disabled={saving} />
        ) : cp.failed ? (
          <p className="text-fail text-xs" role="alert">Couldn't load the page's details. Try again.</p>
        ) : (
          <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">Loading the page's details…</p>
        )
      )}
      {cp.exists && <ContactPageRemove token={code.token} disabled={saving} onRemoved={(row) => (row ? onSave(row) : onCancel())} />}
      {status === "error" && error && (
        <p className="text-fail text-xs" role="alert">
          {error}
          {appealUrl && <> <ReCheckLink href={appealUrl} /></>}
          <MailtoSuggestion
            suggestion={suggestion}
            onApply={(next) => { setValue(next); setSuggestion(null); setStatus("idle"); setError(null) }}
          />
        </p>
      )}
    </form>
  )
}
